// Created on 2023-10-09
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { OccViewer } from "../viewer/viewer.js";
/**
 * Enum for Mouse Keys.
 * It reflects values of corresponding C++ `Aspect_VKeyMouse` enumeration defined by OCCT.
 */
export var MouseKey;
(function (MouseKey) {
    MouseKey[MouseKey["LeftButton"] = 8192] = "LeftButton";
    MouseKey[MouseKey["MiddleButton"] = 16384] = "MiddleButton";
    MouseKey[MouseKey["RightButton"] = 32768] = "RightButton";
})(MouseKey || (MouseKey = {}));
/**
 * Enum for actions that can be performed by the mouse scroll.
 */
export var ScrollActions;
(function (ScrollActions) {
    ScrollActions[ScrollActions["None"] = 0] = "None";
    ScrollActions[ScrollActions["Zoom"] = 1] = "Zoom";
    ScrollActions[ScrollActions["Pan_Vert"] = 2] = "Pan_Vert";
    ScrollActions[ScrollActions["Pan_Horz"] = 3] = "Pan_Horz";
    ScrollActions[ScrollActions["RotateOrbit_Vert"] = 4] = "RotateOrbit_Vert";
    ScrollActions[ScrollActions["RotateOrbit_Horz"] = 5] = "RotateOrbit_Horz";
    ScrollActions[ScrollActions["RotateView_Vert"] = 6] = "RotateView_Vert";
    ScrollActions[ScrollActions["RotateView_Horz"] = 7] = "RotateView_Horz";
})(ScrollActions || (ScrollActions = {}));
/**
 * Enum for Mouse Gestures to be binded with mouse keys.
 * It reflects values of corresponding C++ `AIS_MouseGesture` enumeration defined by OCCT.
 */
export var MouseGesture;
(function (MouseGesture) {
    MouseGesture[MouseGesture["None"] = 0] = "None";
    MouseGesture[MouseGesture["SelectRectangle"] = 1] = "SelectRectangle";
    MouseGesture[MouseGesture["SelectLasso"] = 2] = "SelectLasso";
    MouseGesture[MouseGesture["Zoom"] = 3] = "Zoom";
    MouseGesture[MouseGesture["ZoomWindow"] = 4] = "ZoomWindow";
    MouseGesture[MouseGesture["ZoomVertical"] = 5] = "ZoomVertical";
    MouseGesture[MouseGesture["Pan"] = 6] = "Pan";
    MouseGesture[MouseGesture["RotateOrbit"] = 7] = "RotateOrbit";
    MouseGesture[MouseGesture["RotateView"] = 8] = "RotateView";
    MouseGesture[MouseGesture["Drag"] = 9] = "Drag";
})(MouseGesture || (MouseGesture = {}));
/**
 * Enum for modifier keys that can be combined with Keys/Mouse Keys to create hotkeys.
 * It reflects values of corresponding C++ `Aspect_VKeyFlags` enumeration defined by OCCT.
 */
export var ModifierKeys;
(function (ModifierKeys) {
    ModifierKeys[ModifierKeys["NONE"] = 0] = "NONE";
    ModifierKeys[ModifierKeys["SHIFT"] = 256] = "SHIFT";
    ModifierKeys[ModifierKeys["CTRL"] = 512] = "CTRL";
    ModifierKeys[ModifierKeys["ALT"] = 1024] = "ALT";
    ModifierKeys[ModifierKeys["MENU"] = 2048] = "MENU";
    ModifierKeys[ModifierKeys["META"] = 4096] = "META";
})(ModifierKeys || (ModifierKeys = {}));
/**
 * Enum for Virtual Keys representing keyboard keys.
 * It reflects values of corresponding C++ `Aspect_VKeyBasic` enumeration defined by OCCT.
 */
export var VirtualKeys;
(function (VirtualKeys) {
    VirtualKeys[VirtualKeys["UNKNOWN"] = 0] = "UNKNOWN";
    /** Alphabet Keys */
    VirtualKeys[VirtualKeys["A"] = 1] = "A";
    VirtualKeys[VirtualKeys["B"] = 2] = "B";
    VirtualKeys[VirtualKeys["C"] = 3] = "C";
    VirtualKeys[VirtualKeys["D"] = 4] = "D";
    VirtualKeys[VirtualKeys["E"] = 5] = "E";
    VirtualKeys[VirtualKeys["F"] = 6] = "F";
    VirtualKeys[VirtualKeys["G"] = 7] = "G";
    VirtualKeys[VirtualKeys["H"] = 8] = "H";
    VirtualKeys[VirtualKeys["I"] = 9] = "I";
    VirtualKeys[VirtualKeys["J"] = 10] = "J";
    VirtualKeys[VirtualKeys["K"] = 11] = "K";
    VirtualKeys[VirtualKeys["L"] = 12] = "L";
    VirtualKeys[VirtualKeys["M"] = 13] = "M";
    VirtualKeys[VirtualKeys["N"] = 14] = "N";
    VirtualKeys[VirtualKeys["O"] = 15] = "O";
    VirtualKeys[VirtualKeys["P"] = 16] = "P";
    VirtualKeys[VirtualKeys["Q"] = 17] = "Q";
    VirtualKeys[VirtualKeys["R"] = 18] = "R";
    VirtualKeys[VirtualKeys["S"] = 19] = "S";
    VirtualKeys[VirtualKeys["T"] = 20] = "T";
    VirtualKeys[VirtualKeys["U"] = 21] = "U";
    VirtualKeys[VirtualKeys["V"] = 22] = "V";
    VirtualKeys[VirtualKeys["W"] = 23] = "W";
    VirtualKeys[VirtualKeys["X"] = 24] = "X";
    VirtualKeys[VirtualKeys["Y"] = 25] = "Y";
    VirtualKeys[VirtualKeys["Z"] = 26] = "Z";
    /** Number Keys */
    VirtualKeys[VirtualKeys["k0"] = 27] = "k0";
    VirtualKeys[VirtualKeys["k1"] = 28] = "k1";
    VirtualKeys[VirtualKeys["k2"] = 29] = "k2";
    VirtualKeys[VirtualKeys["k3"] = 30] = "k3";
    VirtualKeys[VirtualKeys["k4"] = 31] = "k4";
    VirtualKeys[VirtualKeys["k5"] = 32] = "k5";
    VirtualKeys[VirtualKeys["k6"] = 33] = "k6";
    VirtualKeys[VirtualKeys["k7"] = 34] = "k7";
    VirtualKeys[VirtualKeys["k8"] = 35] = "k8";
    VirtualKeys[VirtualKeys["k9"] = 36] = "k9";
    /** F Keys */
    VirtualKeys[VirtualKeys["F1"] = 37] = "F1";
    VirtualKeys[VirtualKeys["F2"] = 38] = "F2";
    VirtualKeys[VirtualKeys["F3"] = 39] = "F3";
    VirtualKeys[VirtualKeys["F4"] = 40] = "F4";
    VirtualKeys[VirtualKeys["F5"] = 41] = "F5";
    VirtualKeys[VirtualKeys["F6"] = 42] = "F6";
    VirtualKeys[VirtualKeys["F7"] = 43] = "F7";
    VirtualKeys[VirtualKeys["F8"] = 44] = "F8";
    VirtualKeys[VirtualKeys["F9"] = 45] = "F9";
    VirtualKeys[VirtualKeys["F10"] = 46] = "F10";
    VirtualKeys[VirtualKeys["F11"] = 47] = "F11";
    VirtualKeys[VirtualKeys["F12"] = 48] = "F12";
    /** Standard Keys */
    VirtualKeys[VirtualKeys["UP"] = 49] = "UP";
    VirtualKeys[VirtualKeys["DOWN"] = 50] = "DOWN";
    VirtualKeys[VirtualKeys["LEFT"] = 51] = "LEFT";
    VirtualKeys[VirtualKeys["RIGHT"] = 52] = "RIGHT";
    VirtualKeys[VirtualKeys["PLUS"] = 53] = "PLUS";
    VirtualKeys[VirtualKeys["MINUS"] = 54] = "MINUS";
    VirtualKeys[VirtualKeys["EQUAL"] = 55] = "EQUAL";
    VirtualKeys[VirtualKeys["PAGEUP"] = 56] = "PAGEUP";
    VirtualKeys[VirtualKeys["PAGEDOWN"] = 57] = "PAGEDOWN";
    VirtualKeys[VirtualKeys["HOME"] = 58] = "HOME";
    VirtualKeys[VirtualKeys["END"] = 59] = "END";
    VirtualKeys[VirtualKeys["ESCAPE"] = 60] = "ESCAPE";
    VirtualKeys[VirtualKeys["BACK"] = 61] = "BACK";
    VirtualKeys[VirtualKeys["ENTER"] = 62] = "ENTER";
    VirtualKeys[VirtualKeys["BACKSPACE"] = 63] = "BACKSPACE";
    VirtualKeys[VirtualKeys["SPACE"] = 64] = "SPACE";
    VirtualKeys[VirtualKeys["DELETE"] = 65] = "DELETE";
    VirtualKeys[VirtualKeys["TILDE"] = 66] = "TILDE";
    VirtualKeys[VirtualKeys["TAB"] = 67] = "TAB";
    VirtualKeys[VirtualKeys["COMMA"] = 68] = "COMMA";
    VirtualKeys[VirtualKeys["PERIOD"] = 69] = "PERIOD";
    VirtualKeys[VirtualKeys["SEMICOLON"] = 70] = "SEMICOLON";
    VirtualKeys[VirtualKeys["SLASH"] = 71] = "SLASH";
    VirtualKeys[VirtualKeys["BRACKETLEFT"] = 72] = "BRACKETLEFT";
    VirtualKeys[VirtualKeys["BACKSLASH"] = 73] = "BACKSLASH";
    VirtualKeys[VirtualKeys["BRACKETRIGHT"] = 74] = "BRACKETRIGHT";
    VirtualKeys[VirtualKeys["APOSTROPHE"] = 75] = "APOSTROPHE";
    VirtualKeys[VirtualKeys["NUMLOCK"] = 76] = "NUMLOCK";
    VirtualKeys[VirtualKeys["SCROLLLOCK"] = 77] = "SCROLLLOCK";
    /** Numpad Keys */
    VirtualKeys[VirtualKeys["NUM0"] = 78] = "NUM0";
    VirtualKeys[VirtualKeys["NUM1"] = 79] = "NUM1";
    VirtualKeys[VirtualKeys["NUM2"] = 80] = "NUM2";
    VirtualKeys[VirtualKeys["NUM3"] = 81] = "NUM3";
    VirtualKeys[VirtualKeys["NUM4"] = 82] = "NUM4";
    VirtualKeys[VirtualKeys["NUM5"] = 83] = "NUM5";
    VirtualKeys[VirtualKeys["NUM6"] = 84] = "NUM6";
    VirtualKeys[VirtualKeys["NUM7"] = 85] = "NUM7";
    VirtualKeys[VirtualKeys["NUM8"] = 86] = "NUM8";
    VirtualKeys[VirtualKeys["NUM9"] = 87] = "NUM9";
    VirtualKeys[VirtualKeys["NUMMULTIPLY"] = 88] = "NUMMULTIPLY";
    VirtualKeys[VirtualKeys["NUMADD"] = 89] = "NUMADD";
    VirtualKeys[VirtualKeys["NUMSUBTRACT"] = 90] = "NUMSUBTRACT";
    VirtualKeys[VirtualKeys["NUMDIVIDE"] = 91] = "NUMDIVIDE";
    /** Multimedia Keys */
    VirtualKeys[VirtualKeys["MNEXTTRACK"] = 92] = "MNEXTTRACK";
    VirtualKeys[VirtualKeys["MPREVIOUSTRACK"] = 93] = "MPREVIOUSTRACK";
    VirtualKeys[VirtualKeys["MSTOP"] = 94] = "MSTOP";
    VirtualKeys[VirtualKeys["MPLAYPAUSE"] = 95] = "MPLAYPAUSE";
    VirtualKeys[VirtualKeys["MVOLMUTE"] = 96] = "MVOLMUTE";
    VirtualKeys[VirtualKeys["MVOLDOWN"] = 97] = "MVOLDOWN";
    VirtualKeys[VirtualKeys["MVOLUP"] = 98] = "MVOLUP";
    VirtualKeys[VirtualKeys["MBROWSERBACK"] = 99] = "MBROWSERBACK";
    VirtualKeys[VirtualKeys["MBROWSERFORWARD"] = 100] = "MBROWSERFORWARD";
    VirtualKeys[VirtualKeys["MBROWSERREFRESH"] = 101] = "MBROWSERREFRESH";
    VirtualKeys[VirtualKeys["MBROWSERSTOP"] = 102] = "MBROWSERSTOP";
    VirtualKeys[VirtualKeys["MBROWSERSEARCH"] = 103] = "MBROWSERSEARCH";
    VirtualKeys[VirtualKeys["MBROWSERFAVORITES"] = 104] = "MBROWSERFAVORITES";
    VirtualKeys[VirtualKeys["MBROWSERHOME"] = 105] = "MBROWSERHOME";
})(VirtualKeys || (VirtualKeys = {}));
/**
 * Enum for possible actions to be binded to hotkeys.
 * It reflects values of corresponding C++ `Aspect_VKeyBasic` enumeration defined by OCCT.
 */
export var KeyActions;
(function (KeyActions) {
    /** View Actions */
    KeyActions[KeyActions["ViewTop"] = 106] = "ViewTop";
    KeyActions[KeyActions["ViewBottom"] = 107] = "ViewBottom";
    KeyActions[KeyActions["ViewLeft"] = 108] = "ViewLeft";
    KeyActions[KeyActions["ViewRight"] = 109] = "ViewRight";
    KeyActions[KeyActions["ViewFront"] = 110] = "ViewFront";
    KeyActions[KeyActions["ViewBack"] = 111] = "ViewBack";
    KeyActions[KeyActions["ViewAxoLeftProj"] = 112] = "ViewAxoLeftProj";
    KeyActions[KeyActions["ViewAxoRightProj"] = 113] = "ViewAxoRightProj";
    KeyActions[KeyActions["ViewFitAll"] = 114] = "ViewFitAll";
    KeyActions[KeyActions["ViewRoll90CW"] = 115] = "ViewRoll90CW";
    KeyActions[KeyActions["ViewRoll90CCW"] = 116] = "ViewRoll90CCW";
    KeyActions[KeyActions["ViewSwitchRotate"] = 117] = "ViewSwitchRotate";
    /** Navigation Actions */
    KeyActions[KeyActions["NavInteract"] = 123] = "NavInteract";
    KeyActions[KeyActions["NavForward"] = 124] = "NavForward";
    KeyActions[KeyActions["NavBackward"] = 125] = "NavBackward";
    KeyActions[KeyActions["NavSlideLeft"] = 126] = "NavSlideLeft";
    KeyActions[KeyActions["NavSlideRight"] = 127] = "NavSlideRight";
    KeyActions[KeyActions["NavSlideUp"] = 128] = "NavSlideUp";
    KeyActions[KeyActions["NavSlideDown"] = 129] = "NavSlideDown";
    KeyActions[KeyActions["NavRollCCW"] = 130] = "NavRollCCW";
    KeyActions[KeyActions["NavRollCW"] = 131] = "NavRollCW";
    KeyActions[KeyActions["NavLookLeft"] = 132] = "NavLookLeft";
    KeyActions[KeyActions["NavLookRight"] = 133] = "NavLookRight";
    KeyActions[KeyActions["NavLookUp"] = 134] = "NavLookUp";
    KeyActions[KeyActions["NavLookDown"] = 135] = "NavLookDown";
    KeyActions[KeyActions["NavCrouch"] = 136] = "NavCrouch";
    KeyActions[KeyActions["NavJump"] = 137] = "NavJump";
    KeyActions[KeyActions["NavThrustForward"] = 138] = "NavThrustForward";
    KeyActions[KeyActions["NavThrustBackward"] = 139] = "NavThrustBackward";
    KeyActions[KeyActions["NavThrustStop"] = 140] = "NavThrustStop";
    KeyActions[KeyActions["NavSpeedIncrease"] = 141] = "NavSpeedIncrease";
    KeyActions[KeyActions["NavSpeedDecrease"] = 142] = "NavSpeedDecrease";
})(KeyActions || (KeyActions = {}));
/**
 * Update [[ViewManager]] in OccViewer.
 * @internal
 */
OccViewer.onInit((theModule) => {
    let anInputBindings = theModule.inputBindings;
    Object.defineProperty(anInputBindings, 'mouseBindings', {
        get: function () {
            const aBindings = this._cppGetMouseBindings();
            const aRet = [];
            for (let i = 0; i < aBindings.size(); i++) {
                aRet.push(aBindings.get(i));
            }
            aBindings.delete();
            return aRet;
        },
    });
    Object.defineProperty(anInputBindings, 'scrollBindings', {
        get: function () {
            const aBindings = this._cppGetScrollBindings();
            const aRet = [];
            for (let i = 0; i < aBindings.size(); i++) {
                aRet.push(aBindings.get(i));
            }
            aBindings.delete();
            return aRet;
        },
    });
    Object.defineProperty(anInputBindings, 'keyBindings', {
        get: function () {
            const aBindings = this._cppGetKeysBindings();
            const aRet = [];
            for (let i = 0; i < aBindings.size(); i++) {
                aRet.push(aBindings.get(i));
            }
            aBindings.delete();
            return aRet;
        },
    });
    anInputBindings.getMouseGestureBindings = function (theGesture) {
        //@ts-ignore
        const aBindings = this._cppGetMouseGestureBindings(theGesture);
        const aRet = [];
        for (let i = 0; i < aBindings.size(); i++) {
            aRet.push(aBindings.get(i));
        }
        aBindings.delete();
        return aRet;
    };
    anInputBindings.getScrollActionBindings = function (theGesture) {
        //@ts-ignore
        const aBindings = this._cppGetScrollActionBindings(theGesture);
        const aRet = [];
        for (let i = 0; i < aBindings.size(); i++) {
            aRet.push(aBindings.get(i));
        }
        aBindings.delete();
        return aRet;
    };
    anInputBindings.getActionKeysBindings = function (theAction) {
        //@ts-ignore
        const aBindings = this._cppGetActionKeysBindings(theAction);
        const aRet = [];
        for (let i = 0; i < aBindings.size(); i++) {
            aRet.push(aBindings.get(i));
        }
        aBindings.delete();
        return aRet;
    };
});
